import { UUID } from "../outings";
import { chatText } from "../chat";
import {
  FREESTYLE_ACTIVITIES,
  freestyleObject,
  type FreestyleActivity,
} from "./clips";
export type FeedPeriod = "today" | "week" | "season" | "alltime";
export type FeedFilters = {
  kind: "all" | "squad" | "resort";
  sourceId: string | null;
  activity: "all" | FreestyleActivity;
  sort: "fresh" | "top";
  period: FeedPeriod;
};
export type FeedQuery = FeedFilters | { ids: string[]; period: FeedPeriod };
export const DEFAULT_FEED: FeedFilters = {
  kind: "all",
  sourceId: null,
  activity: "all",
  sort: "fresh",
  period: "alltime",
};
export type SharedClip = {
  id: string;
  revision: number;
  generation: number;
  kind: "squad" | "resort";
  audience: string;
  timezone: string;
  caption: string;
  trick: string;
  activity: FreestyleActivity;
  filmedOn: string;
  publishedAt: string;
  commentsOpen: boolean;
  authorId: string;
  authorName: string;
  own: boolean;
  votes: number;
  voted: boolean;
  voteRevision: number;
  comments: number;
  poster: string;
  expiresAt: string;
};
export type FeedPage = {
  owner: string;
  clips: SharedClip[];
  snapshot: string[] | null;
  limited: boolean;
  checkedAt: string;
};
export type ClipComment = {
  id: string;
  parentId: string | null;
  revision: number;
  body: string;
  createdAt: string;
  editedAt: string | null;
  deleted: boolean;
  authorId: string;
  authorName: string;
  own: boolean;
};
export type CommentPage = {
  owner: string;
  id: string;
  generation: number;
  open: boolean;
  comments: ClipComment[];
  after: string | null;
  checkedAt: string;
};
export type ShareSource = {
  id: string;
  label: string;
  revision: number;
  memberId?: string;
  memberCount?: number;
  rosterHash?: string;
  timezone?: string;
};
export type ShareChoices = {
  owner: string;
  squads: ShareSource[];
  resorts: ShareSource[];
};
export type ClipSharing = {
  revision: number;
  generation: number;
  state: "private" | "pending" | "visible" | "hidden";
  audience: string;
  kind: "squad" | "resort";
  commentsOpen: boolean;
};
export type ShareCommand =
  | {
      action: "share";
      id: string;
      mutationId: string;
      revision: number;
      clipRevision: number;
      kind: "squad" | "resort";
      sourceId: string;
      sourceRevision: number;
      memberId: string | null;
      rosterHash: string | null;
      timezone: string;
      consent: "community-sharing-v1";
    }
  | { action: "withdraw"; id: string; mutationId: string; revision: number }
  | {
      action: "comments";
      id: string;
      mutationId: string;
      revision: number;
      open: boolean;
    };
export type CommentCommand =
  | {
      action: "send";
      clipId: string;
      id: string;
      generation: number;
      mutationId: string;
      revision: 0;
      body: string;
      parentId: string | null;
    }
  | {
      action: "edit";
      clipId: string;
      id: string;
      generation: number;
      mutationId: string;
      revision: number;
      body: string;
    }
  | {
      action: "delete";
      clipId: string;
      id: string;
      generation: number;
      mutationId: string;
      revision: number;
    };
export type ClipReport =
  | {
      action: "report";
      clipId: string;
      generation: number;
      commentId: string | null;
      reason:
        | "abuse"
        | "spam"
        | "privacy"
        | "rights"
        | "unsafe"
        | "age"
        | "other";
      detail: string;
    }
  | { action: "appeal"; caseId: string; revision: number; detail: string };
export type CommunityCommand =
  | ShareCommand
  | {
      action: "vote";
      id: string;
      generation: number;
      revision: number;
      active: boolean;
    }
  | { action: "comment"; command: CommentCommand }
  | { action: "report"; command: ClipReport }
  | { action: "play"; id: string; generation: number };
export type ClipCase = {
  id: string;
  clipId: string;
  commentId: string | null;
  revision: number;
  state: "open" | "closed" | "appealed";
  decision: string | null;
  publicReason: string | null;
  updatedAt: string;
  own: boolean;
  canAppeal: boolean;
};
export type ClipReview = {
  id: string;
  clipId: string;
  commentId: string | null;
  revision: number;
  state: ClipCase["state"];
  evidence: string;
  evidenceRevision: number;
  decision: string | null;
  publicReason: string | null;
  privateReason: string | null;
  appeal: string | null;
  generation: number;
  caseGeneration: number;
  audience: string;
  caption: string;
  trick: string;
  activity: FreestyleActivity;
  filmedOn: string;
  clipRevision: number;
  targetRevision: number;
  currentText: string;
  unavailable: boolean;
  moderationStatus: "pending" | "visible" | "hidden";
  reportCount: number;
};
export type ReviewDecision = {
  action: "approve" | "hide" | "restore" | "no_action";
  caseId: string;
  revision: number;
  targetRevision: number;
  mutationId: string;
  publicReason: string;
  privateReason: string;
};
export type ReviewDetail = {
  id: string;
  createdAt: string;
  reason?: string;
  detail?: string;
  action?: string;
  targetRevision?: number;
  caseRevision?: number;
  publicReason?: string;
  privateReason?: string;
};
const uuid = (v: unknown): v is string => typeof v === "string" && UUID.test(v);
const integer = (v: unknown, min = 0): v is number =>
  typeof v === "number" &&
  Number.isSafeInteger(v) &&
  v >= min &&
  v <= 1_000_000;
const exact = (v: Record<string, unknown>, keys: string[]) =>
  Object.keys(v).length === keys.length &&
  keys.every((k) => Object.hasOwn(v, k));
const period = (v: unknown): v is FeedPeriod =>
  ["today", "week", "season", "alltime"].includes(String(v));
export function validFeedQuery(v: unknown): v is FeedQuery {
  if (!freestyleObject(v) || !period(v.period)) return false;
  if ("ids" in v)
    return (
      exact(v, ["ids", "period"]) &&
      Array.isArray(v.ids) &&
      v.ids.length > 0 &&
      v.ids.length <= 20 &&
      v.ids.every(uuid) &&
      new Set(v.ids).size === v.ids.length
    );
  return (
    exact(v, ["kind", "sourceId", "activity", "sort", "period"]) &&
    ["all", "squad", "resort"].includes(String(v.kind)) &&
    (v.sourceId === null || uuid(v.sourceId)) &&
    ["fresh", "top"].includes(String(v.sort)) &&
    (v.activity === "all" ||
      (typeof v.activity === "string" &&
        Object.hasOwn(FREESTYLE_ACTIVITIES, v.activity)))
  );
}
export function validShareCommand(v: unknown): v is ShareCommand {
  if (
    !freestyleObject(v) ||
    !uuid(v.id) ||
    !uuid(v.mutationId) ||
    !integer(v.revision)
  )
    return false;
  if (v.action === "withdraw")
    return exact(v, ["action", "id", "mutationId", "revision"]);
  if (v.action === "comments")
    return (
      exact(v, ["action", "id", "mutationId", "revision", "open"]) &&
      typeof v.open === "boolean"
    );
  if (
    v.action !== "share" ||
    !exact(v, [
      "action",
      "id",
      "mutationId",
      "revision",
      "clipRevision",
      "kind",
      "sourceId",
      "sourceRevision",
      "memberId",
      "rosterHash",
      "timezone",
      "consent",
    ]) ||
    !integer(v.clipRevision, 1) ||
    !integer(v.sourceRevision, 1) ||
    !uuid(v.sourceId) ||
    v.consent !== "community-sharing-v1" ||
    typeof v.timezone !== "string" ||
    v.timezone.length > 80
  )
    return false;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: v.timezone }).format();
  } catch {
    return false;
  }
  return v.kind === "squad"
    ? v.timezone === "UTC" &&
        uuid(v.memberId) &&
        typeof v.rosterHash === "string" &&
        /^[a-f0-9]{64}$/.test(v.rosterHash)
    : v.kind === "resort" && v.memberId === null && v.rosterHash === null;
}
export function validCommentCommand(v: unknown): v is CommentCommand {
  if (
    !freestyleObject(v) ||
    !uuid(v.clipId) ||
    !uuid(v.id) ||
    !uuid(v.mutationId) ||
    !integer(v.generation, 1) ||
    !integer(v.revision)
  )
    return false;
  const base = [
    "action",
    "clipId",
    "id",
    "generation",
    "mutationId",
    "revision",
  ];
  if (v.action === "delete") return v.revision > 0 && exact(v, base);
  if (!chatText(v.body, 1000, 4000)) return false;
  return v.action === "send"
    ? v.revision === 0 &&
        exact(v, [...base, "body", "parentId"]) &&
        (v.parentId === null || uuid(v.parentId))
    : v.action === "edit" && v.revision > 0 && exact(v, [...base, "body"]);
}
export function validClipReport(v: unknown): v is ClipReport {
  if (!freestyleObject(v) || !chatText(v.detail, 1000, 4000, 0)) return false;
  return v.action === "appeal"
    ? exact(v, ["action", "caseId", "revision", "detail"]) &&
        uuid(v.caseId) &&
        integer(v.revision, 1) &&
        v.detail.trim().length > 0
    : v.action === "report" &&
        exact(v, [
          "action",
          "clipId",
          "generation",
          "commentId",
          "reason",
          "detail",
        ]) &&
        uuid(v.clipId) &&
        integer(v.generation, 1) &&
        (v.commentId === null || uuid(v.commentId)) &&
        [
          "abuse",
          "spam",
          "privacy",
          "rights",
          "unsafe",
          "age",
          "other",
        ].includes(String(v.reason));
}
export function validCommunityCommand(v: unknown): v is CommunityCommand {
  if (!freestyleObject(v)) return false;
  if (["share", "withdraw", "comments"].includes(String(v.action)))
    return validShareCommand(v);
  if (v.action === "comment")
    return exact(v, ["action", "command"]) && validCommentCommand(v.command);
  if (v.action === "report")
    return exact(v, ["action", "command"]) && validClipReport(v.command);
  if (!uuid(v.id) || !integer(v.generation, 1)) return false;
  if (v.action === "play") return exact(v, ["action", "id", "generation"]);
  return (
    v.action === "vote" &&
    exact(v, ["action", "id", "generation", "revision", "active"]) &&
    integer(v.revision) &&
    typeof v.active === "boolean"
  );
}
export function validReviewDecision(v: unknown): v is ReviewDecision {
  return (
    freestyleObject(v) &&
    exact(v, [
      "action",
      "caseId",
      "revision",
      "targetRevision",
      "mutationId",
      "publicReason",
      "privateReason",
    ]) &&
    ["approve", "hide", "restore", "no_action"].includes(String(v.action)) &&
    uuid(v.caseId) &&
    uuid(v.mutationId) &&
    integer(v.revision, 1) &&
    integer(v.targetRevision, 1) &&
    chatText(v.publicReason, 1000, 4000) &&
    chatText(v.privateReason, 2000, 8000, 0)
  );
}
export function readFeedPage(v: unknown, owner: string): FeedPage {
  if (
    !freestyleObject(v) ||
    v.owner !== owner ||
    !Array.isArray(v.clips) ||
    v.clips.length > 20 ||
    typeof v.limited !== "boolean" ||
    typeof v.checkedAt !== "string" ||
    !Number.isFinite(Date.parse(v.checkedAt)) ||
    !(
      v.snapshot === null ||
      (Array.isArray(v.snapshot) &&
        v.snapshot.length <= 100 &&
        v.snapshot.every(uuid) &&
        new Set(v.snapshot).size === v.snapshot.length)
    )
  )
    throw new Error("Your feed could not be checked. Refresh to retry.");
  for (const c of v.clips) {
    if (
      !freestyleObject(c) ||
      !uuid(c.id) ||
      !uuid(c.authorId) ||
      !integer(c.revision, 1) ||
      !integer(c.generation, 1) ||
      !integer(c.votes) ||
      !integer(c.voteRevision) ||
      !integer(c.comments) ||
      typeof c.own !== "boolean" ||
      typeof c.voted !== "boolean" ||
      typeof c.commentsOpen !== "boolean" ||
      !["squad", "resort"].includes(String(c.kind)) ||
      !chatText(c.caption, 500, 2000) ||
      !chatText(c.trick, 80, 320) ||
      !chatText(c.authorName, 80, 320) ||
      !chatText(c.audience, 120, 480) ||
      typeof c.activity !== "string" ||
      !Object.hasOwn(FREESTYLE_ACTIVITIES, c.activity) ||
      typeof c.filmedOn !== "string" ||
      typeof c.publishedAt !== "string" ||
      !Number.isFinite(Date.parse(c.publishedAt)) ||
      typeof c.timezone !== "string" ||
      !validMedia({
        poster: c.poster,
        video: "https://stream.mux.com/check",
        expiresAt: c.expiresAt,
      })
    )
      throw new Error("Your feed could not be checked. Refresh to retry.");
  }
  return v as FeedPage;
}
export function validMedia(
  v: unknown,
): v is { video: string; poster: string; expiresAt: string } {
  if (
    !freestyleObject(v) ||
    typeof v.video !== "string" ||
    typeof v.poster !== "string" ||
    typeof v.expiresAt !== "string"
  )
    return false;
  try {
    return (
      new URL(v.video).origin === "https://stream.mux.com" &&
      new URL(v.poster).origin === "https://image.mux.com" &&
      Date.parse(v.expiresAt) > Date.now() &&
      Date.parse(v.expiresAt) <= Date.now() + 185_000
    );
  } catch {
    return false;
  }
}
